import { useState } from 'react';
import Link from 'next/link';
import { fraunces } from '../Landing/fonts';
import { INK, MUTED, Stamp } from '../Landing/ui';
import JournalTabs from './JournalTabs';

function cardAcceptanceDescription({ cardAcceptance }) {
  let res = 'Card payment is often possible, but not always';
  if (cardAcceptance === 5) res = 'Cards are accepted universally';
  if (cardAcceptance === 4) res = 'Cards are accepted at most places';
  if (cardAcceptance === 2)
    res = 'Cash is king, card payment is only possible at major chains';
  if (cardAcceptance === 1)
    res = 'Cash is king, cards are almost never accepted';
  return res;
}

// Fixed locale: this renders into SSG'd HTML, and the visitor's locale
// would format thousands differently and break hydration.
function price(priceUsd, currency) {
  let res = `US$${priceUsd.toLocaleString('en-US')}`;
  if (currency && currency.dollarExchangeRate !== 1)
    res += ` (${currency.symbol} ${Math.round(
      priceUsd * currency.dollarExchangeRate,
    ).toLocaleString('en-US')})`;
  return res;
}

function ReceiptLine({ category, priceUsd, externalLink }) {
  if (!priceUsd) return null;
  return (
    <li className="tw-flex tw-items-baseline tw-gap-2 tw-py-1.5">
      <span className={`${INK} tw-shrink-0 tw-text-sm`}>
        {externalLink ? (
          <a
            href={externalLink}
            target="_blank"
            rel="nofollow noreferrer noopener"
            className="tw-text-inherit tw-underline tw-decoration-dotted tw-underline-offset-2 hover:tw-text-primary-700 dark:hover:tw-text-primary-300"
          >
            {category}
            <span aria-hidden="true"> ↗</span>
          </a>
        ) : (
          category
        )}
      </span>
      <span
        aria-hidden="true"
        className="tw-mx-1 tw-min-w-4 tw-flex-1 tw-border-0 tw-border-b tw-border-dotted tw-border-[#C9C0AA] dark:tw-border-[#3A4D44]"
      />
      <span
        className={`${INK} tw-shrink-0 tw-text-right tw-font-mono tw-text-[13px]`}
      >
        US${priceUsd.toLocaleString('en-US')}
      </span>
    </li>
  );
}

// The daily budget as a till receipt: traveler-style tabs, dotted-leader
// line items (the booking links are affiliate links — keep them), a
// dashed rule and a Fraunces total. The "money field notes" card beside it
// carries card acceptance and ATM intel.
export default function BudgetBoard({
  locationDetails,
  hotelLink,
  rentalCarLink,
  busLink,
  privateDriverLink,
  taxiLink,
  toursLink,
}) {
  const [style, setStyle] = useState(0);
  const { budget, countryCode, atms } = locationDetails;
  const currency = locationDetails.currencies[0];

  const budgetBackpack = Math.round(
    budget.hostelDormMedianPrice +
      budget.takeawayMeal * 1.5 +
      budget.localPublicTransport +
      budget.longDistanceTransport * 0.3 +
      budget.attractions * 0.5 +
      budget.beerPrice,
  );
  const budgetNomad = Math.round(
    budget.airbnbOneNightMedianPrice +
      budget.deliveryMeal +
      budget.taxiRide +
      budget.coffeePrice,
  );
  const budgetComfort = Math.round(
    budget.hotelMedianPrice +
      budget.rentalCar +
      budget.organizedTour +
      budget.restaurantMeal,
  );
  const budgetLuxury = Math.round(
    budget.fiveStarHotelMedianPrice +
      budget.fineDining +
      budget.privateDriver +
      budget.privateDayTrip,
  );

  const styles = [
    {
      label: 'Backpacker',
      total: budgetBackpack,
      items: [
        {
          category: 'Hostel Dorm',
          priceUsd: budget.hostelDormMedianPrice,
          externalLink: 'https://hostelworld.tp.st/ANPDOIh2',
        },
        { category: 'Takeaway Meal', priceUsd: budget.takeawayMeal },
        { category: 'Public transport', priceUsd: budget.localPublicTransport },
        {
          category: 'Long-distance bus',
          priceUsd: budget.longDistanceTransport,
          externalLink: busLink,
        },
        {
          category: 'Entrance Fee',
          priceUsd: budget.attractions,
          externalLink: 'https://tiqets.tp.st/Lyf4ymwK',
        },
        { category: 'Beer', priceUsd: budget.beerPrice },
      ],
    },
    {
      label: 'Nomad',
      total: budgetNomad,
      items: [
        {
          category: countryCode === 'cu' ? 'Casa Particular' : 'Apartment',
          priceUsd: budget.airbnbOneNightMedianPrice,
          externalLink: 'https://vrbo.tp.st/66qlj8bj',
        },
        { category: 'Delivery Meal', priceUsd: budget.deliveryMeal },
        {
          category: 'Short Taxi Ride',
          priceUsd: budget.taxiRide,
          externalLink: taxiLink,
        },
        { category: 'Coffee', priceUsd: budget.coffeePrice },
      ],
    },
    {
      label: 'Comfort',
      total: budgetComfort,
      items: [
        {
          category: 'Hotel',
          priceUsd: budget.hotelMedianPrice,
          externalLink: hotelLink,
        },
        {
          category: 'Rental Car',
          priceUsd: budget.rentalCar,
          externalLink: rentalCarLink,
        },
        {
          category: 'Organized Tour',
          priceUsd: budget.organizedTour,
          externalLink: toursLink,
        },
        { category: 'Restaurant', priceUsd: budget.restaurantMeal },
      ],
    },
    {
      label: 'Luxury',
      total: budgetLuxury,
      items: [
        {
          category: '5 Star Hotel',
          priceUsd: budget.fiveStarHotelMedianPrice,
          externalLink: hotelLink,
        },
        { category: 'Fine Dining', priceUsd: budget.fineDining },
        {
          category: 'Private transport',
          priceUsd: budget.privateDriver,
          externalLink: privateDriverLink,
        },
        {
          category: 'Private day trip',
          priceUsd: budget.privateDayTrip,
          externalLink: toursLink,
        },
      ],
    },
  ];

  const active = styles[style];

  // Money intel as loose notes pinned beside the receipt — a stack of
  // small cards with varied tilts instead of one half-empty box.
  const notes = [
    { category: 'Cards', value: cardAcceptanceDescription(locationDetails) },
  ];
  if (atms && atms.atmFees)
    notes.push({
      category: 'ATM Fees',
      value: `${
        atms.atmFees === 3
          ? 'All ATMs charge fees'
          : atms.atmFees === 2
          ? 'Most ATMs charge fees, but you can find fee-free ATMs'
          : 'Most ATMs charge no fees'
      }${atms.comment ? `. ${atms.comment}` : ''}`,
    });
  if (atms && atms.feeFreeAtms && atms.feeFreeAtms.length > 0)
    notes.push({ category: 'Free ATMs', value: atms.feeFreeAtms.join(', ') });
  if (atms && atms.expensiveAtms && atms.expensiveAtms.length > 0)
    notes.push({
      category: 'ATMs to avoid (high fees)',
      value: atms.expensiveAtms.join(', '),
    });

  return (
    <div>
      <JournalTabs
        ariaLabel="Traveler style"
        options={styles.map((s, i) => ({
          label: `${s.label} ${'$'.repeat(i + 1)}`,
        }))}
        value={style}
        onChange={setStyle}
      />

      <div className="tw-mt-8 tw-grid tw-items-start tw-gap-10 lg:tw-grid-cols-[minmax(0,24rem)_1fr] lg:tw-gap-14">
        {/* The receipt — laid on the desk at a slight angle */}
        <div className="tw-relative -tw-rotate-[0.8deg] tw-rounded-t-xl tw-border tw-border-b-2 tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] [border-bottom-style:dashed] tw-bg-white dark:tw-bg-[#15231E] tw-px-6 tw-pb-7 tw-pt-5 tw-shadow-[0_30px_60px_-28px_rgba(29,45,40,0.55)]">
          <div className="tw-border-0 tw-border-b-[3px] tw-border-double tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-pb-3 tw-text-center">
            <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
              Est. daily fares
            </p>
            <p className="tw-m-0 tw-mt-1 tw-font-mono tw-text-[10px] tw-tracking-[0.2em] tw-text-[#8A958F]">
              {locationDetails.title.toUpperCase()}
              {currency && currency.dollarExchangeRate !== 1
                ? ` · US$1 = ${currency.symbol}${currency.dollarExchangeRate}`
                : ''}
            </p>
          </div>

          <ul className="tw-m-0 tw-mt-3 tw-list-none tw-p-0">
            {active.items.map(item => (
              <ReceiptLine key={item.category} {...item} />
            ))}
          </ul>

          <div className="tw-relative tw-mt-3 tw-border-0 tw-border-t tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-pt-4">
            <Stamp
              aria-hidden="true"
              className="tw-pointer-events-none tw-absolute -tw-top-4 tw-right-0 tw-h-20 tw-w-20 tw-rotate-12 tw-text-primary-700 tw-opacity-[0.12] dark:tw-text-primary-300"
              text="ESTIMATE · FIELD RATES · "
            />
            <p className="tw-m-0 tw-flex tw-items-baseline tw-justify-between tw-gap-3">
              <span className="tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-[#8A958F]">
                Daily total
              </span>
              <span
                className={`${fraunces.className} tw-text-3xl tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300`}
              >
                {price(active.total, currency)}
              </span>
            </p>
          </div>
        </div>

        {/* Money field notes — loose pinned notes beside the receipt */}
        <div className="lg:tw-pt-2">
          <p className="tw-m-0 tw-mb-5 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
            Money field notes
          </p>
          <div className="tw-flex tw-flex-col tw-gap-4">
            {notes.map((note, i) => (
              <div
                key={note.category}
                className={`tw-relative tw-max-w-xl tw-rounded-xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-px-5 tw-py-4 tw-shadow-[0_18px_36px_-28px_rgba(29,45,40,0.55)] ${
                  i % 2
                    ? 'tw-rotate-[0.6deg] lg:tw-ml-12'
                    : '-tw-rotate-[0.5deg] lg:tw-mr-12'
                }`}
              >
                {/* Brass pin */}
                <span
                  aria-hidden="true"
                  className="tw-absolute -tw-top-1.5 tw-left-1/2 tw-h-3 tw-w-3 -tw-translate-x-1/2 tw-rounded-full tw-border tw-border-solid tw-border-[rgba(120,85,30,0.55)] tw-bg-[#D9B26A] tw-shadow-[0_2px_4px_rgba(29,45,40,0.35)]"
                />
                <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-[#8A958F]">
                  {note.category}
                </p>
                <p className="tw-m-0 tw-mt-1 tw-text-[15px] tw-leading-relaxed tw-text-[#33403B] dark:tw-text-[#C7D2CC]">
                  {note.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p
        className={`${MUTED} tw-m-0 tw-mt-6 tw-font-mono tw-text-[10px] tw-tracking-[0.18em]`}
      >
        THIS TRAVEL GUIDE MAY CONTAIN{' '}
        <Link
          href="/about/affiliate-disclosure"
          prefetch={false}
          className="tw-text-inherit tw-underline tw-decoration-dotted tw-underline-offset-2 hover:tw-text-primary-700"
        >
          AFFILIATE LINKS
        </Link>
      </p>
    </div>
  );
}
